import React, { useContext, useEffect, useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
} from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import { getDocs, query, orderBy } from "firebase/firestore";
import PropertyContext from "../contexts/PropertyContext";
import { propertyCollection } from "../utils/firestorePaths";

const OccupancyHistoryWidget = () => {
  const { selectedProperty, selectedClient, rooms } = useContext(PropertyContext);
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dayRange, setDayRange] = useState(7);

  // Get date string in YYYY-MM-DD format (local timezone)
  const getDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Generate array of dates for the selected range
  const generateDateRange = (days) => {
    const dates = [];
    const today = new Date();
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      dates.push(getDateString(date));
    }
    return dates;
  };

  // Calculate current room status counts
  const currentStatusCounts = useMemo(() => {
    const counts = { Up: 0, Occupied: 0, Down: 0, Other: 0 };
    rooms.forEach((room) => {
      const status = room.status;
      if (status === "Up") counts.Up++;
      else if (status === "Occupied") counts.Occupied++;
      else if (status === "Down") counts.Down++;
      else counts.Other++;
    });
    return counts;
  }, [rooms]);

  useEffect(() => {
    if (!selectedProperty || rooms.length === 0) {
      setLoading(false);
      return;
    }

    const fetchHistoryData = async () => {
      setLoading(true);
      try {
        // Fetch propertyActionHistory documents
        const historyRef = propertyCollection(
          selectedClient,
          selectedProperty,
          "propertyActionHistory"
        );
        const historyQuery = query(historyRef, orderBy("date", "desc"));
        const snapshot = await getDocs(historyQuery);

        // Build a map of date -> actions
        const actionsByDate = {};
        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          actionsByDate[data.date] = data.actions || [];
        });

        // Generate date range
        const dateRange = generateDateRange(dayRange);
        const totalRooms = rooms.length;

        // Start with current counts and work backwards
        let runningCounts = { ...currentStatusCounts };
        const chartData = [];

        // Process dates from most recent to oldest
        for (let i = dateRange.length - 1; i >= 0; i--) {
          const dateStr = dateRange[i];
          const isToday = i === dateRange.length - 1;

          if (isToday) {
            // Today uses current room data
            chartData.unshift({
              date: dateStr,
              dateLabel: formatDateLabel(dateStr),
              up: Math.round((runningCounts.Up / totalRooms) * 10000) / 100,
              occupied: Math.round((runningCounts.Occupied / totalRooms) * 10000) / 100,
              down: Math.round(((runningCounts.Down + runningCounts.Other) / totalRooms) * 10000) / 100,
            });
          } else {
            // For past days, reverse the changes that happened on the NEXT day
            // to get what the status was at the END of this day
            const nextDateStr = dateRange[i + 1];
            const nextDayActions = actionsByDate[nextDateStr] || [];

            // Reverse each action to get previous day's end state
            nextDayActions.forEach((action) => {
              if (action.actionType === "room_status_change") {
                const prevStatus = action.previousStatus;
                const newStatus = action.newStatus;

                // Decrement the new status (undo the change)
                if (newStatus === "Up") runningCounts.Up = Math.max(0, runningCounts.Up - 1);
                else if (newStatus === "Occupied") runningCounts.Occupied = Math.max(0, runningCounts.Occupied - 1);
                else if (newStatus === "Down") runningCounts.Down = Math.max(0, runningCounts.Down - 1);
                else runningCounts.Other = Math.max(0, runningCounts.Other - 1);

                // Increment the previous status (restore the old state)
                if (prevStatus === "Up") runningCounts.Up++;
                else if (prevStatus === "Occupied") runningCounts.Occupied++;
                else if (prevStatus === "Down") runningCounts.Down++;
                else runningCounts.Other++;
              }
            });

            chartData.unshift({
              date: dateStr,
              dateLabel: formatDateLabel(dateStr),
              up: Math.round((runningCounts.Up / totalRooms) * 10000) / 100,
              occupied: Math.round((runningCounts.Occupied / totalRooms) * 10000) / 100,
              down: Math.round(((runningCounts.Down + runningCounts.Other) / totalRooms) * 10000) / 100,
            });
          }
        }

        setHistoryData(chartData);
      } catch (error) {
        console.error("Error fetching occupancy history:", error);
      }
      setLoading(false);
    };

    fetchHistoryData();
  }, [selectedClient, selectedProperty, rooms, dayRange, currentStatusCounts]);

  // Format date for x-axis labels
  const formatDateLabel = (dateStr) => {
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const handleDayRangeChange = (event, newRange) => {
    if (newRange !== null) {
      setDayRange(newRange);
    }
  };

  if (loading) {
    return (
      <Card sx={{ height: "100%" }}>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6" component="div">
            Occupancy History
          </Typography>
          <ToggleButtonGroup
            value={dayRange}
            exclusive
            onChange={handleDayRangeChange}
            size="small"
          >
            <ToggleButton value={7}>7 Days</ToggleButton>
            <ToggleButton value={14}>14 Days</ToggleButton>
            <ToggleButton value={30}>30 Days</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Chart */}
        {historyData.length > 0 ? (
          <Box sx={{ width: "100%", height: 300 }}>
            <LineChart
              xAxis={[
                {
                  data: historyData.map((d) => d.dateLabel),
                  scaleType: "point",
                  tickLabelStyle: {
                    fontSize: 11,
                  },
                },
              ]}
              yAxis={[
                {
                  min: 0,
                  max: 100,
                  tickNumber: 5,
                  valueFormatter: (value) => `${value}%`,
                },
              ]}
              series={[
                {
                  data: historyData.map((d) => d.up),
                  label: "Available",
                  color: "#4caf50",
                  curve: "linear",
                  showMark: dayRange <= 14,
                },
                {
                  data: historyData.map((d) => d.occupied),
                  label: "Occupied",
                  color: "#2196f3",
                  curve: "linear",
                  showMark: dayRange <= 14,
                },
                {
                  data: historyData.map((d) => d.down),
                  label: "Down/Other",
                  color: "#f44336",
                  curve: "linear",
                  showMark: dayRange <= 14,
                },
              ]}
              height={280}
              margin={{ top: 20, bottom: 30, left: 50, right: 20 }}
              slotProps={{
                legend: {
                  direction: "row",
                  position: { vertical: "top", horizontal: "right" },
                  padding: 0,
                  itemMarkWidth: 10,
                  itemMarkHeight: 10,
                  markGap: 5,
                  itemGap: 15,
                  labelStyle: {
                    fontSize: 12,
                  },
                },
              }}
            />
          </Box>
        ) : (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography variant="body2" color="text.secondary">
              No history data available
            </Typography>
          </Box>
        )}

        {/* Legend note */}
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1, textAlign: "center" }}>
          Percentages based on {rooms.length} total rooms
        </Typography>
      </CardContent>
    </Card>
  );
};

export default OccupancyHistoryWidget;
